import type { AdminReportMediaDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Playable `<video>` for a report media row (AUT-272 — the admin must be
 * able to WATCH walkaround clips, not just see that one exists).
 *
 * Plain HTML5 `<video>` (via MUI `Box component="video"`) — no player
 * dependency. The source is the same 1h-TTL signed S3 URL the customer
 * app streams, so nothing new is minted server-side.
 *
 * Load-bearing details:
 *   - `poster={thumbUrl}` — the pipeline generates a video thumb @1s, so
 *     the operator sees the first frame instead of a black rectangle
 *     while metadata loads. `null` thumb (job still queued) simply means
 *     no poster; the element stays playable either way.
 *   - `preload="metadata"` — a walkaround clip is multi-megabyte and the
 *     operator may never press play; fetch the duration, not the file.
 *   - `key` on the element is the CALLER's job (Lightbox keys by media
 *     id) so navigating between two clips remounts rather than swapping
 *     `src` under a playing element.
 *   - `onError` bubbles up so the parent can invalidate the detail query
 *     and re-mint an expired URL (the same recovery photos use).
 */
export interface ReportVideoProps {
  media: AdminReportMediaDto;
  /** Accessible name, e.g. "видео 1" from the gallery's running index. */
  label: string;
  onError?: () => void;
  sx?: SxProps<Theme>;
}

export const ReportVideo = ({ media, label, onError, sx }: ReportVideoProps): ReactElement => {
  const { t } = useTranslation();

  return (
    <Box
      component="video"
      src={media.signedUrl}
      {...(media.thumbUrl !== null ? { poster: media.thumbUrl } : {})}
      controls
      preload="metadata"
      playsInline
      aria-label={t('reports.mediaTab.videoPlayerLabel', { label })}
      {...(onError ? { onError } : {})}
      sx={sx}
    >
      {t('reports.mediaTab.videoUnsupported')}
    </Box>
  );
};
